import PropTypes from 'prop-types';
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';

import { Map, Iterable, List } from 'immutable';

import Alert      from '@mui/material/Alert';
import Grid       from '@mui/material/Grid';
import Button     from '@mui/material/Button';
import Box        from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Upload     from '@mui/icons-material/Upload';

import Dropzone from 'react-dropzone';
import Lottie from 'react-lottie';

import File from './File';
import Display from 'components/Generic/Display';
import { generateRandomId } from 'helpers/string';

import emptyAnimation from 'assets/landing_page/file_upload/empty.json';
import errorAnimation from 'assets/landing_page/file_upload/error.json';
import processingAnimation from 'assets/landing_page/file_upload/processing.json';
import successAnimation from 'assets/landing_page/file_upload/success.json';

import {
  PROCESSING_STATUS,
  UNRECOGNIZED_STATUS,
  PARTIALLY_RECOGNIZED_STATUS,
  UPLOAD_ERROR_STATUS,
  RECOGNITION_ERROR_STATUS,
  EMPTY_STATUS,
  SUCCESS_STATUS,
  FAILED_STATUS,
} from './constants';

const STATUS_BY_PRIORITY = [PROCESSING_STATUS, UPLOAD_ERROR_STATUS, RECOGNITION_ERROR_STATUS, FAILED_STATUS, UNRECOGNIZED_STATUS, PARTIALLY_RECOGNIZED_STATUS];
const ALLOW_NEXT_STATUSES = [UNRECOGNIZED_STATUS, RECOGNITION_ERROR_STATUS, SUCCESS_STATUS, PARTIALLY_RECOGNIZED_STATUS, FAILED_STATUS];
const PREVENT_NEXT_STATUSES = [UPLOAD_ERROR_STATUS, PROCESSING_STATUS, EMPTY_STATUS];

const ANIMATION_DEFAULT_OPTIONS = {
  loop: true,
  autoplay: true,
};

const ERROR_ANIMATION = <Lottie options={{ ...ANIMATION_DEFAULT_OPTIONS, animationData: errorAnimation }} />;

const GIF_ICONS = {
  empty: <Lottie options={{ ...ANIMATION_DEFAULT_OPTIONS, animationData: emptyAnimation }} />,
  success: <Lottie options={{ ...ANIMATION_DEFAULT_OPTIONS, animationData: successAnimation }} />,
  processing: <Lottie options={{ ...ANIMATION_DEFAULT_OPTIONS, animationData: processingAnimation }} />,

  unrecognized: ERROR_ANIMATION,
  partially_recognized: ERROR_ANIMATION,
  upload_error: ERROR_ANIMATION,
  recognition_error: ERROR_ANIMATION,
  failed: ERROR_ANIMATION,
}

const MAX_RETRIES = 3;

const LIMIT = 5;
const TYPES = [
  'text/plain',
  'text/csv',
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/gif',
];

function coherceStatusToState(status) {
  switch (status) {
    case RECOGNITION_ERROR_STATUS:
    case FAILED_STATUS:
      return 'pending'; // WetDog Error
    default:
      return status;
  }
}

function FileUpload({ name, value, onChange, disabled, documentName, country, recognize, maxRetries = MAX_RETRIES }) {
  const [state, setState] = useState(new Map());
  const [files, setFiles] = useState(new List());
  const rejectedFiles = state.get('rejectedFiles', new List());
  const toUpload = state.get('toUpload', new List());
  const uploaded = state.get('uploaded', new List());
  const status = state.get('status', EMPTY_STATUS);
  const retries = state.get('retries', 0);

  const maxRetriesReached = useMemo(() => retries >= maxRetries, [retries]);

  useEffect(() => {
    if (files.size || value === null || !value.get('blobs')) return;

    setFiles(value.get('blobs'));
  }, [value]);

  function removeExcessFiles(files) {
    if (files.size <= LIMIT) return files;

    return files.slice(0, LIMIT);
  }

  function onRetryAccepted (acceptedFiles) {
    const filtered = new List(acceptedFiles.map((file) => new Map({ blob: file, id: generateRandomId(), status: PROCESSING_STATUS })));

    const currentValidFiles = files.filter(
      (file) => !(file.getIn(['error', 'recognition']) || [UNRECOGNIZED_STATUS, PARTIALLY_RECOGNIZED_STATUS, FAILED_STATUS].includes(file.get('status')) || file.getIn(['error', 'upload']))
    );

    const allFiles = removeExcessFiles(filtered.concat(currentValidFiles));
    const newFiles = allFiles.filter((file) => file.get('status') === PROCESSING_STATUS);

    setState(state.set('toUpload', newFiles).set('uploaded', new List()).set('retries', status === UPLOAD_ERROR_STATUS ? retries : retries + 1));
    setFiles(allFiles);
  }

  function onDropAccepted(acceptedFiles) {
    const filtered = new List(acceptedFiles.map((file) => new Map({ blob: file, id: generateRandomId(), status: PROCESSING_STATUS })));

    const allFiles = removeExcessFiles(filtered.concat(files));
    const newFiles = allFiles.filter((file) => file.get('status') === PROCESSING_STATUS);

    setState(state.set('toUpload', newFiles).set('uploaded', new List()));
    setFiles(allFiles);
  }

  function onDropRejected(rejectedFiles) {
    const rejected = rejectedFiles.map((file) => {
      const errors = file.errors.map((e) => t(e.code.replace(/-/g, '_'), { scope: 'eos.landing_page.text.upload', defaultValue: e.message }));

      return { name: file.file.name, errors: errors.join(', ') };
    });

    setState(state.set('rejectedFiles', rejected));
  }

  useEffect(() => {
    if (uploaded.size === 0) return;

    if (uploaded.size !== toUpload.size) return;

    const newFiles = files.map(
      (file) => {
        const index = uploaded.findIndex((uploaded) => uploaded.get('id') === file.get('id'));
        if (index === -1) return file;

        return uploaded.get(index);
      }
    );

    setFiles(newFiles);
  }, [uploaded, files, toUpload]);

  useEffect(() => {
    let status = files.size ? SUCCESS_STATUS : EMPTY_STATUS;

    const statuses = files.map((file) => file.get('status'));
    for (let index = 0; index < STATUS_BY_PRIORITY.length; index++) {
      if (statuses.includes(STATUS_BY_PRIORITY[index])) {
        status = STATUS_BY_PRIORITY[index];
        break;
      }
    }

    const currentIds = files.map((file) => file.get('id'));
    const newToUpload = toUpload.filter(
      (file) => currentIds.includes(file.get('id'))
    );

    const newUploaded = uploaded.filter(
      (file) => currentIds.includes(file.get('id'))
    );

    setState(state.set('status', status)
      .set('toUpload', newToUpload)
      .set('uploaded', newUploaded)
      .set('rejectedFiles', new List()));

    if (ALLOW_NEXT_STATUSES.includes(status)) {
      const blobs = files.map((file) => file.set('recognition', coherceStatusToState(file.get('status'))));
      onChange(name, new Map({ blobs }));
      return;
    }

    if (PREVENT_NEXT_STATUSES.includes(status)){
      onChange(name, new Map());
    }
  }, [files]);

  const onUpload = useCallback((file) => {
    setState(state.set('uploaded', uploaded.push(file)).set('rejectedFiles', new List()));
  }, [uploaded, state, toUpload]);

  const dropzoneParams = {
    accept:   TYPES.join(', '),
    minSize:  1,
    maxSize:  10000000, // 10mb
    disabled: disabled || files.length >= LIMIT,
  };

  const removeFile = useCallback((id) => {
    if (!files) return;

    const index = files.findIndex((file) => file.get('id') === id);

    const newFiles = index !== -1 ? files.delete(index) : files;

    setFiles(newFiles);
    setState(state.set('rejectedFiles', new List()));
  }, [files]);

  const rejectedFilesList = useMemo(() => {
    return rejectedFiles.map(
      (file) => (
        <Alert key={file.name} severity="error" sx={{ mt: 2 }}>
          <Typography><b>{file.name}</b>: {file.errors}</Typography>
        </Alert>
      )
    );
  }, [rejectedFiles]);

  function DropZone({ onDropAccepted, onDropRejected, text, id, icon, variant = 'contained' }) {
    return (
      <Dropzone
        {...dropzoneParams}
        onDropAccepted={onDropAccepted}
        onDropRejected={onDropRejected}
      >
        {({ getRootProps, getInputProps }) => (
          <div data-testid={`${id}-dropzone`} {...getRootProps()}>
            <input {...getInputProps()} />
            <Button
              variant={variant}
              component="span"
              color="primary"
              onClick={null}
              data-testid={id}
              disabled={dropzoneParams.disabled}
              startIcon={icon}
            >
              {text}
            </Button>
          </div>
        )}
      </Dropzone>
    );
  }

  return (
    <Grid item xs={12}>
      <Box sx={{
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', px: 0, py: 0,
      }}
      >
        <Box sx={{ alignItems: 'center', height: '30vh', width: '30vh' }}>
          {GIF_ICONS[status]}
        </Box>

        <Box
          data-testid="accepted-files"
          sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', py: 3,
          }}
        >
          {files.map((file) => (
            <Box key={file.get('id')} sx={{ pb: 2, width: '100%' }}>
              <File object={file} onUpload={onUpload} removeFile={removeFile} documentName={documentName} country={country} disabled={retries >= maxRetries} recognize={recognize} />
            </Box>
          ))}
        </Box>

        <Display if={EMPTY_STATUS === status}>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', pb: 3 }}>
            <DropZone
              onDropAccepted={onDropAccepted}
              onDropRejected={onDropRejected}
              text={t('eos.landing_page.label.upload')}
              icon={<Upload />}
              id="file-upload-upload-button"
            />
          </Box>
        </Display>

        <Display if={[SUCCESS_STATUS].includes(status) && recognize}>
          <Typography variant="caption" sx={{ textAlign: 'center' }}>{t(status, { scope: 'eos.landing_page.text.upload' })}</Typography>
        </Display>

        <Display if={[RECOGNITION_ERROR_STATUS, UNRECOGNIZED_STATUS, PARTIALLY_RECOGNIZED_STATUS, UPLOAD_ERROR_STATUS, FAILED_STATUS].includes(status)}>
          <Typography variant="caption" sx={{ textAlign: 'center', fontWeight: 'bold' }}>{t(`eos.landing_page.text.upload.error.${maxRetriesReached ? 'max_retries' : status}.title`)}</Typography>
          <Typography variant="caption" sx={{ textAlign: 'center', pb: 2 }}>{t(`eos.landing_page.text.upload.error.${maxRetriesReached ? 'max_retries' : status}.text`)}</Typography>
        </Display>

        <Display if={[UPLOAD_ERROR_STATUS].includes(status) || ([UNRECOGNIZED_STATUS, PARTIALLY_RECOGNIZED_STATUS, RECOGNITION_ERROR_STATUS, FAILED_STATUS].includes(status) && !maxRetriesReached)}>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <DropZone
              onDropAccepted={onRetryAccepted}
              onDropRejected={onDropRejected}
              variant={[UPLOAD_ERROR_STATUS].includes(status) ? 'outlined' : 'contained'}
              text={t('eos.landing_page.label.upload_again')}
              id="file-upload-retry-upload-button"
            />
          </Box>
        </Display>

        {rejectedFilesList}
      </Box>
    </Grid>
  );
}

FileUpload.defaultProps = {
  name:         null,
  value:        null,
  onChange:     null,
  disabled:     false,
  documentName: null,
  country:      null,
  recognize:    false,
};

FileUpload.propTypes = {
  name:         PropTypes.string,
  value:        PropTypes.objectOf(Iterable),
  onChange:     PropTypes.func,
  disabled:     PropTypes.bool,
  documentName: PropTypes.string,
  country:      PropTypes.string,
  recognize:    PropTypes.bool,
};

export default FileUpload;
